/**
 * Observability initialization for Business Web.
 *
 * Uses the canonical browser init from @tonnie/observability/browser.
 * PostHog + Sentry keys can be empty — observability is a no-op until configured.
 */

import type { IAnalyticsClient, IErrorClient } from '@tonnie/observability';
import { initObservabilityBrowser } from '@tonnie/observability/browser';
import { runtimeEnv } from '@/core/config/runtime-env';
import { OBSERVABILITY_ENV, OBSERVABILITY_RELEASE } from './observability-env';

const POSTHOG_API_KEY = runtimeEnv.get('VITE_POSTHOG_API_KEY') || '';
const POSTHOG_HOST = runtimeEnv.get('VITE_POSTHOG_HOST') || 'https://eu.posthog.com';
const SENTRY_DSN = runtimeEnv.get('VITE_SENTRY_DSN') || '';
const OBSERVABILITY_DEBUG = runtimeEnv.get('VITE_OBSERVABILITY_DEBUG') === 'true';

let _analyticsClient: IAnalyticsClient | null = null;
let _errorClient: IErrorClient | null = null;
let _captureCustomEvent:
  ((eventName: string, properties?: Record<string, unknown>) => void) | null = null;

/**
 * Initialize observability once before React renders.
 * Call from main.tsx before createRoot.
 */
export function initBusinessObservability(): void {
  if (_analyticsClient !== null) return;

  const result = initObservabilityBrowser({
    posthogApiKey: POSTHOG_API_KEY,
    posthogHost: POSTHOG_HOST,
    portal: 'business',
    env: OBSERVABILITY_ENV,
    release: OBSERVABILITY_RELEASE,
    sentryDsn: SENTRY_DSN,
    debug: OBSERVABILITY_DEBUG,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,
  });

  _analyticsClient = result.analyticsClient;
  _errorClient = result.errorClient;
  _captureCustomEvent = result.captureCustomEvent;
}

export function getAnalyticsClient(): IAnalyticsClient {
  if (!_analyticsClient) {
    initBusinessObservability();
    if (!_analyticsClient)
      throw new Error('[Observability] init failed — analytics client is null');
  }
  return _analyticsClient;
}

export function getErrorClient(): IErrorClient {
  if (!_errorClient) {
    initBusinessObservability();
    if (!_errorClient) throw new Error('[Observability] init failed — error client is null');
  }
  return _errorClient;
}

export function captureCustomEvent(eventName: string, properties?: Record<string, unknown>): void {
  try {
    if (!_captureCustomEvent) initBusinessObservability();
    _captureCustomEvent?.(eventName, properties);
  } catch {
    // Never block the user flow for analytics
  }
}
